import { useState } from 'react';
import { Plane } from 'lucide-react';

/**
 * FlightProgressTimeline
 * 
 * Horizontal timeline showing aircraft progression through OOOI phases:
 * INBOUND → OUT → OFF → ON → IN
 * 
 * Features:
 * - Aircraft icon rotates at each position (down, flat, up, flat, flat)
 * - Color coded by delay status (green/yellow/red)
 * - Clickable to show flight details modal
 */
const FlightProgressTimeline = ({ 
  currentFlight, 
  previousFlight,
  onShowDetails,
  className = '' 
}) => {
  // Determine current phase based on actual times
  const getCurrentPhase = () => {
    if (!currentFlight) return 'INBOUND';
    
    const now = new Date();
    const leg = currentFlight;
    
    // Parse times (handle different formats)
    const getTime = (timeObj) => {
      if (!timeObj) return null;
      if (timeObj.utc) return new Date(timeObj.utc);
      if (typeof timeObj === 'string') return new Date(timeObj);
      return null;
    };
    
    const schedOut = getTime(leg.sched_out);
    const actualOut = getTime(leg.actual_out);
    const schedIn = getTime(leg.sched_in);
    const actualIn = getTime(leg.actual_in);
    
    // Determine phase
    if (actualIn && now >= actualIn) {
      return 'IN'; // Landed and taxiing to gate
    }
    
    if (actualOut && now >= actualOut) {
      if (schedIn && now >= schedIn) {
        return 'ON'; // Should be landing
      }
      return 'OFF'; // In flight
    }
    
    if (schedOut && now >= schedOut) {
      return 'OUT'; // Should have pushed back
    }
    
    return 'INBOUND'; // Before departure
  };
  
  // Calculate delay in minutes
  const getDelayMinutes = () => {
    if (!currentFlight) return 0;
    
    const leg = currentFlight;
    const getTime = (timeObj) => {
      if (!timeObj) return null;
      if (timeObj.utc) return new Date(timeObj.utc);
      if (typeof timeObj === 'string') return new Date(timeObj);
      return null;
    };
    
    const schedOut = getTime(leg.sched_out);
    const actualOut = getTime(leg.actual_out);
    
    if (!schedOut || !actualOut) return 0;
    
    const delayMs = actualOut - schedOut;
    return Math.round(delayMs / (1000 * 60));
  };
  
  // Get color based on delay
  const getDelayColor = () => {
    const delayMins = getDelayMinutes();
    
    if (delayMins <= 15) return 'text-green-500'; // On time
    if (delayMins <= 45) return 'text-yellow-500'; // Minor delay
    return 'text-red-500'; // Major delay
  };
  
  // Get aircraft rotation based on phase
  const getAircraftRotation = (phase) => {
    switch (phase) {
      case 'INBOUND': return 'rotate-90'; // Pointed down
      case 'OUT': return 'rotate-0'; // Flat right
      case 'OFF': return '-rotate-45'; // Angled up
      case 'ON': return 'rotate-0'; // Flat right
      case 'IN': return 'rotate-0'; // Flat right
      default: return 'rotate-0';
    }
  };
  
  // Get position percentage along timeline
  const getAircraftPosition = (phase) => {
    switch (phase) {
      case 'INBOUND': return '0%';
      case 'OUT': return '25%';
      case 'OFF': return '50%';
      case 'ON': return '75%';
      case 'IN': return '100%';
      default: return '0%';
    }
  };
  
  const currentPhase = getCurrentPhase();
  const delayColor = getDelayColor();
  const rotation = getAircraftRotation(currentPhase);
  const position = getAircraftPosition(currentPhase);
  
  // Position markers
  const markers = [
    { id: 'INBOUND', label: 'INBOUND', position: '0%' },
    { id: 'OUT', label: 'OUT', position: '25%' },
    { id: 'OFF', label: 'OFF', position: '50%' },
    { id: 'ON', label: 'ON', position: '75%' },
    { id: 'IN', label: 'IN', position: '100%' }
  ];
  
  return (
    <div className={`relative w-full px-4 py-6 ${className}`}>
      {/* Timeline track */}
      <div className="relative h-1 bg-slate-300 dark:bg-slate-700 rounded-full">
        {/* Position markers */}
        {markers.map((marker) => (
          <div
            key={marker.id}
            className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2"
            style={{ left: marker.position }}
          >
            {/* Dot */}
            <div
              className={`w-3 h-3 rounded-full ${
                marker.id === currentPhase
                  ? `${delayColor.replace('text-', 'bg-')} ring-2 ring-white dark:ring-slate-900`
                  : 'bg-slate-400 dark:bg-slate-600'
              }`}
            />
            
            {/* Label */}
            <div className="absolute top-6 left-1/2 -translate-x-1/2 whitespace-nowrap">
              <span
                className={`text-xs font-medium ${
                  marker.id === currentPhase
                    ? delayColor
                    : 'text-slate-500 dark:text-slate-400'
                }`}
              >
                {marker.label}
              </span>
            </div>
          </div>
        ))}
        
        {/* Aircraft icon */}
        <button
          onClick={onShowDetails}
          className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 z-10 
                     transition-all duration-500 ease-in-out
                     hover:scale-110 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-full p-1"
          style={{ left: position }}
          aria-label="Show flight details"
        >
          <Plane
            className={`w-8 h-8 ${delayColor} ${rotation} transition-all duration-500`}
            strokeWidth={2}
          />
        </button>
      </div>
      
      {/* Delay indicator */}
      {getDelayMinutes() > 0 && (
        <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 whitespace-nowrap">
          <span className={`text-xs font-medium ${delayColor}`}>
            {getDelayMinutes()} min delay
          </span>
        </div>
      )}
    </div>
  );
};

export default FlightProgressTimeline;
